<template>
  <div class="vh-100 d-flex align-items-center justify-content-center bg-light px-3">
    <div class="login-card shadow-lg w-100 d-flex">
      <div class="row g-0 w-100 h-100">
        <!-- Left Panel (Empty space to let the background image show) -->
        <div class="col-md-7 d-none d-md-block" />

        <!-- Right Panel (Form) -->
        <div class="col-md-5 d-flex align-items-center justify-content-center">
          <div
            class="auth-form-container form-glass d-flex flex-column"
            style="gap: 24px;"
          >
            <div class="text-center">
              <h2 class="fw-bold mb-2">
                Create an Account
              </h2>
              <p class="text-muted mb-0">
                Join PetQueen today for free.
              </p>
            </div>

            <form
              class="d-flex flex-column"
              style="gap: 24px;"
              @submit.prevent="handleSignup"
            >
              <div>
                <label class="form-label text-muted small fw-semibold text-uppercase mb-1">Email Address</label>
                <input
                  v-model="email"
                  type="email"
                  class="form-control form-control-lg bg-white border-0 shadow-sm"
                  placeholder="e.g. hello@petqueen.com"
                  required
                >
                <div
                  v-if="email && !isEmailValid"
                  class="text-danger small mt-1"
                >
                  Please enter a valid email address.
                </div>
              </div>
              
              <div>
                <label class="form-label text-muted small fw-semibold text-uppercase mb-1">Password</label>
                <div class="input-group shadow-sm rounded">
                  <input
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control form-control-lg bg-white border-0"
                    placeholder="••••••••"
                    required
                  >
                  <button
                    type="button"
                    class="btn btn-white bg-white border-0 text-muted px-3"
                    @click="togglePassword"
                  >
                    <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" />
                  </button>
                </div>
                <div
                  v-if="password && !isPasswordStrong"
                  class="text-danger small mt-1"
                >
                  Your password must be at least 8 characters long and include a number, an uppercase letter, and a special character.
                </div>
              </div>

              <div>
                <label class="form-label text-muted small fw-semibold text-uppercase mb-1">Confirm Password</label>
                <div class="input-group shadow-sm rounded">
                  <input
                    v-model="confirmPassword"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-control form-control-lg bg-white border-0"
                    placeholder="••••••••"
                    required
                  >
                  <button
                    type="button"
                    class="btn btn-white bg-white border-0 text-muted px-3"
                    @click="toggleConfirmPassword"
                  >
                    <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" />
                  </button>
                </div>
                <div
                  v-if="passwordMismatch"
                  class="text-danger small mt-1"
                >
                  Passwords do not match!
                </div>
              </div>

              <div
                v-if="error"
                class="alert alert-danger py-2 small mb-0"
              >
                {{ error }}
              </div>

              <button
                type="submit"
                class="btn btn-primary text-white w-100 py-3 fw-bold rounded-3 shadow-sm"
                :disabled="loading || passwordMismatch || (email && !isEmailValid) || (password && !isPasswordStrong)"
              >
                <span
                  v-if="loading"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                {{ loading ? 'Creating Account...' : 'Sign Up' }}
              </button>
            </form>

            <p class="text-center text-muted small mb-0">
              Already have an account? 
              <router-link
                to="/login"
                class="text-primary text-decoration-none fw-bold"
              >
                Sign In
              </router-link>
            </p>
            <div class="text-center">
              <router-link
                to="/"
                class="text-muted text-decoration-none small"
              >
                <i class="fas fa-arrow-left me-1" /> Back to Homepage
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useRouter, useRoute } from 'vue-router';
import { doc, collection, writeBatch, setDoc, serverTimestamp } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';

const email = ref('');
const password = ref('');
const confirmPassword = ref('');
const error = ref(null);
const loading = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

const toggleConfirmPassword = () => {
  showConfirmPassword.value = !showConfirmPassword.value;
};

// Email format validation
const isEmailValid = computed(() => {
  if (!email.value) return true;
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(email.value);
});

const passwordMismatch = computed(() => {
  return password.value && confirmPassword.value && password.value !== confirmPassword.value;
});

const isPasswordStrong = computed(() => {
  if (!password.value) return true;
  const regex = /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;
  return regex.test(password.value);
});

const handleSignup = async () => {
  if (!isEmailValid.value) {
    error.value = 'Please enter a valid email address.';
    return;
  }

  if (passwordMismatch.value) {
    error.value = 'Passwords do not match!';
    return;
  }

  if (!isPasswordStrong.value) {
    error.value = 'Your password does not meet the security criteria.';
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email.value, password.value);
    const user = userCredential.user;

    const userData = {
      uid: user.uid,
      email: user.email,
      displayName: user.email.split('@')[0],
      photoURL: null,
      phoneNumber: null,
      address: null,
      createdAt: serverTimestamp(),
      account_number: Date.now(),
    };

    try {
      await userStore.setUserData(user.uid, userData);
      await userStore.fetchUserProfile(user.uid);
    } catch (storeError) {
      console.error('Store save failed:', storeError);
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, userData);
    }

    const accountCollection = collection(db, 'users', user.uid, 'account');
    const accountDocs = [
      { id: 'properties', type: 'Member', ad_limit: 3, verified: false },
      { id: 'pet_account', type: 'Personal', verified: false },
      { id: 'service_account', type: 'Personal', verified: false },
    ];

    const batch = writeBatch(db);
    accountDocs.forEach((account) => {
      const docRef = doc(accountCollection, account.id);
      batch.set(docRef, account);
    });
    await batch.commit();

    const redirectPath = route.query.redirect || '/';
    router.push(redirectPath);
  } catch (err) {
    if (err.code === 'auth/email-already-in-use') {
      error.value = 'This email is already in use. Please log in.';
    } else {
      error.value = 'An error occurred during signup: ' + err.message;
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.login-card {
  max-width: 1000px;
  min-height: 750px;
  background-image: url('https://firebasestorage.googleapis.com/v0/b/petqueen-v3.firebasestorage.app/o/public%2Fassets%2FpetqueenLogin.jpeg?alt=media&token=f7581352-c414-4ddc-9882-e31af58bf227');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 20px;
  overflow: hidden;
}

.auth-form-container {
  width: 100%;
  max-width: 450px;
  padding: 24px;
}

/* Glass effect for mobile so form is readable over image */
.form-glass {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
}

/* Remove glass effect on desktop because the right side of the image is already blank/cream */
@media (min-width: 768px) {
  .form-glass {
    background: transparent;
    box-shadow: none;
  }
}

.form-control:focus {
  box-shadow: none;
  background-color: #fff !important;
  border: 1px solid #dee2e6 !important;
}

.btn-primary {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

.btn-primary:hover {
  background-color: #2563eb;
  border-color: #2563eb;
}
</style>
